import type {FC} from 'react';
import React from 'react';

import {Button, useToast} from '@tempo/core';
import {Formik} from 'formik';
import {object} from 'yup';
import {useEmailSchema} from '@app/hooks/useSchema';
import {useTranslation} from '@app/hooks/useTranslation';
import {Form} from '@components/Form/Form';
import {usePasswordReset} from '@app/hooks/usePasswordReset';
import {FormControlField} from '../Form/FormField';
import {useFocusFirstEmptyField} from '@app/hooks/useFocusFirstEmptyField';
import {TitleDescription, highlightText} from '../Layouts/elements';
import {logClick} from '@app/modules/tracking/gtm';
import {logCenter} from '@app/modules/tracking/logCenter';
import {useAppState} from '@app/modules/apps/context';
import {useClientId} from '@app/hooks/useClient';
import {NextSeo} from 'next-seo';

const PasswordResetForm: FC = () => {
	const [emailSchema] = useEmailSchema();
	const toast = useToast();
	const {status, email, sendResetPasswordRequest} = usePasswordReset();
	const {isOauth} = useAppState();
	const clientId = useClientId();
	const t = useTranslation();
	useFocusFirstEmptyField();

	if (status === 'success') {
		if (isOauth) {
			logCenter.log({
				type: 'cdp',
				eventName: 'screen_view',
				customAttributes: {
					screen_view_name: 'oauth_email_reset_password',
					screen_view_id: clientId,
				},
			});
		}

		return (
			<>
				<NextSeo title={t('formal.youvegotmail')} />
				<TitleDescription
					title={t('formal.youvegotmail')}
					detail={t('formal.checkinboxlinksenttoXemail', {
						sprintf: [highlightText(email)],
					})}
				/>
			</>
		);
	}

	return (
		<>
			<NextSeo title={t('formal.resetyourpassword')} />
			<TitleDescription title={t('formal.resetyourpassword')} />
			<Formik
				initialValues={{
					email: '',
				}}
				validateOnBlur={false}
				validateOnChange={false}
				onSubmit={async (values) => {
					logClick({
						eventaction: 'send-link',
					});
					isOauth &&
						logCenter.log({
							type: 'cdp',
							eventName: 'oauth_reset_password',
							customAttributes: {
								action: 'send_link',
							},
						});
					sendResetPasswordRequest(values).catch((error) => {
						toast({
							title: error.name,
							description: error.message,
							status: 'error',
							duration: 5000,
							isClosable: true,
						});
					});
				}}
				validationSchema={object().shape({
					email: emailSchema,
				})}
			>
				<Form>
					<FormControlField
						id="email"
						name="email"
						type="email"
						data-testid="email-field"
						label={t('common.label_email')}
						hint={t('formal.enteremailtoreceivelink')}
					/>
					<Button type="submit" width="100%" data-testid="receive-link-button">
						{t('common.action_sendlink')}
					</Button>
				</Form>
			</Formik>
		</>
	);
};

export default PasswordResetForm;
